import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Plus, Trash2, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportFormatEditor = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/export-format`);
      setConfig(res.data);
    } catch { toast.error("Failed to load export format"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/export-format`, config);
      toast.success("Export format saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset export format to defaults?")) return;
    try {
      const res = await axios.post(`${API}/export-format/reset`);
      setConfig(res.data);
      toast.success("Reset to defaults");
    } catch { toast.error("Failed to reset"); }
  };

  const toggleSection = (index) => {
    setConfig(prev => {
      const sections = [...prev.sections];
      sections[index] = { ...sections[index], enabled: !sections[index].enabled };
      return { ...prev, sections };
    });
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config.sections.length) return;
    setConfig(prev => {
      const sections = [...prev.sections];
      [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
      return { ...prev, sections: sections.map((s, i) => ({ ...s, order: i + 1 })) };
    });
  };

  const addCustomField = () => {
    setConfig(prev => ({
      ...prev,
      custom_fields: [...(prev.custom_fields || []), { label: '', default_value: '' }]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setConfig(prev => {
      const fields = [...(prev.custom_fields || [])];
      fields[index] = { ...fields[index], [field]: value };
      return { ...prev, custom_fields: fields };
    });
  };

  const removeCustomField = (index) => {
    setConfig(prev => ({
      ...prev,
      custom_fields: (prev.custom_fields || []).filter((_, i) => i !== index)
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} /></div>;
  if (!config) return null;

  return (
    <div className="animate-fadeIn" data-testid="export-format-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link><span>&rsaquo;</span><span>Export Format</span>
      </div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Export Format Editor</h1>
          <p className="page-subtitle">Customize which sections appear in exported Word and PDF documents</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" onClick={handleReset} style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} style={{ background: 'var(--navy)', color: '#fff' }} data-testid="save-export-format-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Format
          </Button>
        </div>
      </div>

      {/* Header & Footer */}
      <div className="form-card" style={{ marginBottom: '20px' }}>
        <div className="form-body-inner" style={{ padding: '20px' }}>
          <div className="form-grid-2">
            <div>
              <Label>Document Header Text</Label>
              <Input value={config.header_text || ''} onChange={e => setConfig(prev => ({ ...prev, header_text: e.target.value }))}
                data-testid="header-text-input" />
            </div>
            <div>
              <Label>Document Footer Text</Label>
              <Input value={config.footer_text || ''} onChange={e => setConfig(prev => ({ ...prev, footer_text: e.target.value }))} />
            </div>
          </div>
        </div>
      </div>

      {/* Sections Toggle & Reorder */}
      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)', marginBottom: '12px' }}>
        Document Sections
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        Toggle sections on/off and drag to reorder. Disabled sections will not appear in exported documents.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '24px' }}>
        {config.sections?.map((section, index) => (
          <div key={section.key} className="form-card" style={{
            marginBottom: 0, opacity: section.enabled ? 1 : 0.5,
            border: section.enabled ? '1px solid var(--gray-border)' : '1px dashed var(--gray-border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px' }}>
              <GripVertical className="w-4 h-4" style={{ color: 'var(--text-light)', flexShrink: 0 }} />
              <span style={{
                fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'var(--text-muted)',
                background: 'var(--gray-light)', padding: '2px 8px', borderRadius: '4px', minWidth: '28px', textAlign: 'center'
              }}>
                {index + 1}
              </span>
              <span style={{ flex: 1, fontWeight: 500, fontSize: '14px' }}>{section.label}</span>
              <div style={{ display: 'flex', gap: '4px' }} >
                <button className="icon-btn" onClick={() => moveSection(index, -1)} disabled={index === 0}
                  style={{ opacity: index === 0 ? 0.3 : 1 }}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button className="icon-btn" onClick={() => moveSection(index, 1)} disabled={index === config.sections.length - 1}
                  style={{ opacity: index === config.sections.length - 1 ? 0.3 : 1 }}>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  className="icon-btn"
                  onClick={() => toggleSection(index)}
                  title={section.enabled ? 'Disable' : 'Enable'}
                  style={{ color: section.enabled ? 'var(--green)' : 'var(--red)', borderColor: section.enabled ? 'var(--green)' : 'var(--red)' }}
                  data-testid={`toggle-section-${section.key}`}
                >
                  {section.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section Headings */}
      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)', marginBottom: '8px' }}>
        Section Headings in Export
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        Customize the heading text for each section as it appears in Word and PDF exports.
      </p>
      <div className="form-card" style={{ marginBottom: '24px' }}>
        <div className="form-body-inner" style={{ padding: '20px', display: 'grid', gap: '12px' }}>
          {[
            { key: 'heading_scope', label: 'Scope of Work', defaultVal: 'SCOPE OF SERVICES' },
            { key: 'heading_vendor_conditions', label: 'Vendor Conditions', defaultVal: 'CONDITIONS TO BE SATISFIED BY VENDOR' },
            { key: 'heading_facilities', label: 'Facilities Provided', defaultVal: 'FACILITIES & SUPPORT PROVIDED BY GNOSIS' },
            { key: 'heading_special_conditions', label: 'Special Conditions', defaultVal: 'SPECIAL CONDITIONS / EXCLUSIONS' },
            { key: 'heading_maintenance', label: 'Maintenance SLA', defaultVal: 'MAINTENANCE TERMS & SLA' },
            { key: 'heading_equipment', label: 'Equipment Schedule', defaultVal: 'EQUIPMENT SCHEDULE' },
            { key: 'heading_rates_manpower', label: 'Manpower Rates', defaultVal: 'SCHEDULE OF RATES — MANPOWER' },
            { key: 'heading_rates_card', label: 'Rate Card', defaultVal: 'SCHEDULE OF RATES — RATE CARD' },
          ].map(h => (
            <div key={h.key} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Label style={{ minWidth: '160px', fontSize: '13px' }}>{h.label}</Label>
              <Input 
                value={(config.section_headings || {})[h.key] || h.defaultVal}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  section_headings: { ...(prev.section_headings || {}), [h.key]: e.target.value }
                }))}
                data-testid={`heading-${h.key}`}
                style={{ flex: 1 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Custom Fields */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)' }}>Custom Fields</div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Add extra fields that appear in all exported documents</p>
        </div>
        <Button variant="outline" size="sm" onClick={addCustomField} style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}
          data-testid="add-custom-field-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Field
        </Button>
      </div>

      {(config.custom_fields || []).length > 0 && (
        <div className="form-card" style={{ marginBottom: '24px' }}>
          <div className="form-body-inner" style={{ padding: '16px 20px' }}>
            {config.custom_fields.map((field, index) => (
              <div key={index} style={{
                display: 'flex', gap: '12px', alignItems: 'center', padding: '8px 0',
                borderBottom: index < config.custom_fields.length - 1 ? '1px solid var(--gray-border)' : 'none'
              }}>
                <Input value={field.label} onChange={e => updateCustomField(index, 'label', e.target.value)}
                  placeholder="Field Label (e.g. Engineer In-Charge)" style={{ flex: 1 }} />
                <Input value={field.default_value} onChange={e => updateCustomField(index, 'default_value', e.target.value)}
                  placeholder="Default Value (optional)" style={{ flex: 1 }} />
                <button className="icon-btn" onClick={() => removeCustomField(index)} style={{ color: 'var(--red)' }}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '40px' }}>
        <Button variant="outline" onClick={handleReset} style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
          <RotateCcw className="w-4 h-4 mr-2" /> Reset
        </Button>
        <Button onClick={handleSave} disabled={saving} style={{ background: 'var(--navy)', color: '#fff' }}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Format
        </Button>
      </div>
    </div>
  );
};

export default ExportFormatEditor;
